import { useEffect } from 'react';

/**
 * Puts the unread count in the browser tab title: "(3) Orders — Kamil Store".
 *
 * The bell badge only exists while the dashboard tab is the one being looked
 * at. Most of the day it is not — it sits behind the shop's other tabs, and a
 * new order is a chime nobody is in the room to hear. The tab title is the one
 * piece of this app that stays readable from another tab, so it is where the
 * count belongs.
 *
 * Two things have to be reconciled, and doing only one of them is why this
 * needs a component rather than a line:
 *
 *   1. The count changes on its own, every 20s, from NotificationBar's poll.
 *   2. The title changes on its own, on every navigation, from
 *      `helpers/projectTitle.js` — which writes `document.title` directly and
 *      knows nothing about a prefix.
 *
 * So the prefix is re-applied whenever either moves. A MutationObserver on the
 * <title> node catches the navigation case; writes this component makes itself
 * are ignored, or the observer would answer its own change forever.
 */

/** "(12) " at the very start. Kept here so stripping and writing cannot drift. */
const BADGE_PATTERN = /^\(\d+\)\s+/;

const stripBadge = (title) => String(title || '').replace(BADGE_PATTERN, '');

export default function AdminTitleBadge() {
  useEffect(() => {
    const titleEl = document.querySelector('title');
    if (!titleEl) return undefined;

    // The title as the app means it, without whatever we last prefixed.
    let baseTitle = stripBadge(document.title);
    let unread = Number(window.__ksAdminNotificationCounts?.total) || 0;
    // Set while we write, so the observer can tell our own change from the
    // app's. Without it every write triggers a mutation, which triggers a
    // write.
    let writing = false;

    const apply = () => {
      const next = unread > 0 ? `(${unread}) ${baseTitle}` : baseTitle;
      if (document.title === next) return;
      writing = true;
      document.title = next;
      // Cleared on a microtask rather than immediately: the observer fires
      // asynchronously, so clearing it on this line would be too early and the
      // guard would never hold.
      Promise.resolve().then(() => {
        writing = false;
      });
    };

    const onCounts = (event) => {
      unread = Number(event?.detail?.total) || 0;
      apply();
    };

    const observer = new MutationObserver(() => {
      if (writing) return;
      baseTitle = stripBadge(document.title);
      apply();
    });
    observer.observe(titleEl, { childList: true, characterData: true, subtree: true });

    window.addEventListener('ks:admin-notification-counts', onCounts);
    apply();

    return () => {
      observer.disconnect();
      window.removeEventListener('ks:admin-notification-counts', onCounts);
      // Leave the tab title as the app wants it, not with a stale count on it.
      document.title = stripBadge(document.title);
    };
  }, []);

  return null;
}
